import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  X, Sparkles, Zap, AlertTriangle, TrendingUp, ChevronRight,
  Lightbulb, Target, Rocket, CheckCircle2, Info
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";

/**
 * AI Copilot Component
 * Provides context-aware suggestions, next-step recommendations, and guidance
 * Designed to be extremely easy to use - even a 10-year-old can build workflows!
 */
export default function AICopilot({ 
  nodes = [], 
  edges = [], 
  selectedNode = null,
  onAddNode,
  onClose,
  visible = true
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [quickActions, setQuickActions] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [completeness, setCompleteness] = useState(0);
  const [timeEstimate, setTimeEstimate] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (visible) {
      fetchSuggestions();
    }
  }, [nodes, edges, selectedNode, visible]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/copilot/suggestions`, {
        nodes,
        edges,
        selected_node: selectedNode,
        last_action: "node_added"
      }, { withCredentials: true });

      setSuggestions(response.data.suggestions || []);
      setQuickActions(response.data.quick_actions || []);
      setWarnings(response.data.warnings || []);
      setCompleteness(response.data.workflow_completeness || 0);
      setTimeEstimate(response.data.estimated_time_to_complete || "");
    } catch (error) {
      console.error('Copilot error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    if (suggestion.type === "add_trigger" && suggestion.options) {
      // Show trigger options
      if (onAddNode) {
        onAddNode({
          type: "start",
          data: { label: "Start", trigger_type: suggestion.options[0].value }
        });
      }
    } else if (suggestion.type === "next_step" && suggestion.options) {
      // User clicks on an option
      // This will be handled by the option buttons
    } else if (suggestion.action) {
      // Execute action
      if (onAddNode) {
        onAddNode({ type: "action", data: { label: "New Step" } });
      }
    }
  };

  const handleOptionClick = async (option) => {
    if (onAddNode) {
      // Map option value to node type
      const nodeTypeMap = {
        "ai_analyze": { type: "ai", label: "AI Analyze" },
        "slack_notify": { type: "action", label: "Send to Slack", app: "slack" },
        "sheets_save": { type: "action", label: "Save to Sheets", app: "sheets" },
        "email_reply": { type: "email", label: "Send Email" },
        "generate_report": { type: "ai", label: "Generate Report" },
        "sync_data": { type: "database", label: "Sync Data" },
        "send_reminder": { type: "email", label: "Send Reminder" }
      };

      const nodeConfig = nodeTypeMap[option.value] || { type: "action", label: "New Step" };
      onAddNode(nodeConfig);
    }
  };

  if (!visible) return null;

  return (
    <Card className="fixed bottom-6 right-6 w-96 shadow-2xl border-2 border-primary/20 z-50 glass-card backdrop-blur-lg">
      {/* Header */}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg font-heading">AI Copilot</CardTitle>
              <p className="text-xs text-muted-foreground">Your workflow assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExpanded(!expanded)}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              className="h-8 w-8 p-0 hover:bg-red-500/10"
              data-testid="copilot-close-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        {expanded && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Workflow Progress</span>
              <span className="font-medium text-primary">{Math.round(completeness)}%</span>
            </div>
            <Progress value={completeness} className="h-2" />
            {timeEstimate && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Target className="w-3 h-3" />
                {timeEstimate} to complete
              </p>
            )}
          </div>
        )}
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          <ScrollArea className="h-[400px] pr-3">
            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="mb-4 space-y-2">
                {warnings.map((warning, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30"
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                          {warning.title}
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                          {warning.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Suggestions */}
            <div className="space-y-3">
              {suggestions.length === 0 && !loading && (
                <div className="text-center py-8">
                  <Rocket className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground">
                    Looking great! Keep building your workflow.
                  </p>
                </div>
              )}

              {loading && (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-sm text-muted-foreground">Thinking...</p>
                </div>
              )}

              {suggestions.map((suggestion, index) => (
                <Card
                  key={index}
                  className="border-2 border-transparent hover:border-primary/30 transition-all cursor-pointer"
                  data-testid={`suggestion-${index}`}
                >
                  <CardContent className="p-4">
                    {/* Suggestion Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        {suggestion.type === "add_trigger" && <Target className="w-4 h-4 text-primary" />}
                        {suggestion.type === "next_step" && <ChevronRight className="w-4 h-4 text-primary" />}
                        {suggestion.type === "integration_suggestion" && <Zap className="w-4 h-4 text-primary" />}
                        {suggestion.type === "pattern_suggestion" && <Lightbulb className="w-4 h-4 text-primary" />}
                        {!["add_trigger", "next_step", "integration_suggestion", "pattern_suggestion"].includes(suggestion.type) && (
                          <Info className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm text-foreground mb-1">
                          {suggestion.title}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {suggestion.description}
                        </p>
                      </div>
                      {suggestion.confidence && (
                        <Badge variant="outline" className="text-xs">
                          {Math.round(suggestion.confidence * 100)}%
                        </Badge>
                      )}
                    </div>

                    {/* Options */}
                    {suggestion.options && (
                      <div className="space-y-2 mt-3">
                        {suggestion.options.map((option, optIndex) => (
                          <button
                            key={optIndex}
                            onClick={() => handleOptionClick(option)}
                            className="w-full text-left p-3 rounded-lg bg-muted/30 hover:bg-muted/60 transition-all border border-transparent hover:border-primary/30"
                            data-testid={`option-${optIndex}`}
                          >
                            <div className="flex items-center gap-2">
                              {option.icon && (
                                <span className="text-lg">{option.icon}</span>
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium text-foreground">
                                  {option.label}
                                </p>
                                {option.description && (
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {option.description}
                                  </p>
                                )}
                              </div>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Integrations */}
                    {suggestion.integrations && (
                      <div className="space-y-2 mt-3">
                        {suggestion.integrations.map((integration, intIndex) => (
                          <div
                            key={intIndex}
                            className="p-2 rounded-lg bg-muted/30 border border-border"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{integration.icon}</span>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-foreground">
                                  {integration.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {integration.reason}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {integration.difficulty}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Action Button */}
                    {suggestion.action && !suggestion.options && (
                      <Button
                        size="sm"
                        className="w-full mt-3"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        Apply Suggestion
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick Actions */}
            {quickActions.length > 0 && (
              <>
                <Separator className="my-4" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Quick Actions
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {quickActions.map((action, index) => (
                      <Button
                        key={index}
                        size="sm"
                        variant="outline"
                        className="justify-start text-xs h-auto py-2"
                        onClick={() => {
                          if (onAddNode) {
                            onAddNode({ type: "action", data: { label: action.label } });
                          }
                        }}
                      >
                        <span className="mr-1">{action.icon}</span>
                        <span className="truncate">{action.label}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Completion Celebration */}
            {completeness >= 90 && (
              <div className="mt-4 p-4 rounded-lg bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                  <div>
                    <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                      🎉 Almost there!
                    </p>
                    <p className="text-xs text-green-600/80 dark:text-green-400/80">
                      Your workflow is looking great. Test it out!
                    </p>
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      )}
    </Card>
  );
}
